import React from 'react'
import {Column} from 'react-table'
import {log} from '@/logger'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Switch,
} from '../../utils/chakraMonomorphic'

import {PageTemplate} from '../../templates/PageTemplate/PageTemplate'
import {
  BlankSlate,
  LoadingState,
  PageHeader,
  Table,
  Visible,
} from '../../components'
import {useAuth, useRequiredAuth} from '../../stores/AuthStore'
import {useStatusToast} from '../../app/ToastManager'
import {useConfirmation} from '../../app/ConfirmationManager'
import {useFeatureFlag} from '../../hooks/useFeatureFlag'
import {fetchHiredStaff} from '../../services/api/staff/fetchHiredStaff'
import {featureSettings} from '../../services/api/featuresettings'
import {PageNotFoundView} from '../PageNotFoundView/PageNotFoundView'
import {HowItWorksVideoModal} from './HowItWorksVideoModal'
import copy from './copy.json'
import {CONFIGCAT_FLAG_KEY} from './constants'

// CONFIGCAT_FLAG_KEY is the development rollout gate. The customer
// entitlement check (`enable-campanion-staff-engagement-features`) lives
// on the route in Router.tsx; this flag is what hides the panel while
// it's still being staged in. When both are on the view renders normally;
// if the flag is off the view 404s so direct-URL access during rollout is
// harmless. It is re-used in SideMenuTemplate to hide the nav entry.

// FeatureID for "name-that-camper-staff" in the shared FeatureSettings.Feature
// table. Owned by the persons entitlements rule of the same name. This row's ID
// is 61 across all environments (the earlier prod/test discrepancy was resolved
// when the DB team aligned both on 61, 2026-06).
const NAME_THAT_CAMPER_STAFF_FEATURE_ID = 61

interface StaffRow {
  ID: string
  name: string
  // Years of tenure as reported by /v0/staff. 1 means this is the staffer's
  // first year; null when the API omits the value. Used by the first-year
  // quick filter.
  years: number | null
  nameThatCamper: boolean
  // PersonTargetFeature row id when the toggle is on; null when off. Captured
  // so we know what to DELETE on toggle-off.
  nameThatCamperRowID: number | null
  // PersonID stored on the PersonTargetFeature row. For self-service staff
  // features we set this equal to TargetPersonID (the staffer) so the row
  // reflects "the staff person has permission to use this feature on
  // themselves," preserving the table's PersonID = actor convention. The
  // DELETE handler echoes this back verbatim to match the row in SQL.
  nameThatCamperGranterID: number | null
}

export const CampanionStaffAccessView: React.FC<
  React.PropsWithChildren
> = () => {
  const {client} = useRequiredAuth()
  const {handleRefreshSession} = useAuth()
  const {toast} = useStatusToast()
  const showConfirmation = useConfirmation()
  const {value: panelEnabled, loading: flagLoading} = useFeatureFlag(
    CONFIGCAT_FLAG_KEY,
    false,
  )
  const [sort, setSort] = React.useState([{id: 'name', desc: false}])
  const [staff, setStaff] = React.useState<StaffRow[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [firstYearOnly, setFirstYearOnly] = React.useState(false)
  const [videoOpen, setVideoOpen] = React.useState(false)
  // Per-row in-flight lock: holds the StaffRow.ID of any row whose toggle has
  // a pending create/delete request. The Switch is disabled while pending so
  // rapid clicks can't fire a second request that would race with the first.
  const [pending, setPending] = React.useState<Set<string>>(new Set())

  const markPending = React.useCallback((rowID: string): void => {
    setPending((prev) => {
      const next = new Set(prev)
      next.add(rowID)
      return next
    })
  }, [])

  const clearPending = React.useCallback((rowID: string): void => {
    setPending((prev) => {
      const next = new Set(prev)
      next.delete(rowID)
      return next
    })
  }, [])

  // Merge a partial update into a single staff row by ID, leaving the rest
  // untouched. Both toggle paths (grant/revoke) flip the same trio of fields,
  // so they share this helper instead of repeating the map.
  const patchRow = React.useCallback(
    (rowID: string, patch: Partial<StaffRow>): void => {
      setStaff((prev) =>
        prev.map((r) => (r.ID === rowID ? {...r, ...patch} : r)),
      )
    },
    [],
  )

  // The load effect below calls handleRefreshSession(), which dispatches auth-
  // state changes that re-render this subtree. If the effect's dependency array
  // included render-identity-unstable values, that re-render would re-fire the
  // effect, which would re-exchange the session again — an unbounded loop that
  // hammers /v1/auth/exchange (observed in release testing for AB#19213). The
  // classic offender is `toast`: Chakra v1's useToast() returns a fresh
  // reference every render, which defeats useStatusToast's useMemo, so `toast`
  // identity is not actually stable in the running app even though it looks
  // memoized. We therefore read both `toast` and `handleRefreshSession` through
  // refs and keep the dependency array limited to the logical inputs
  // (client/season/flag), so the effect only re-runs when those actually
  // change — never as a side effect of its own re-exchange.
  const toastRef = React.useRef(toast)
  React.useEffect(() => {
    toastRef.current = toast
  }, [toast])
  const handleRefreshSessionRef = React.useRef(handleRefreshSession)
  React.useEffect(() => {
    handleRefreshSessionRef.current = handleRefreshSession
  }, [handleRefreshSession])

  React.useEffect(() => {
    if (flagLoading || !panelEnabled) return undefined
    let cancelled = false
    const fetchAll = (): Promise<
      [
        Awaited<ReturnType<typeof fetchHiredStaff>>,
        Awaited<ReturnType<typeof featureSettings.listPersonTargetFeatures>>,
      ]
    > =>
      Promise.all([
        fetchHiredStaff({
          clientID: client.ID,
          seasonID: client.currentSeasonID,
        }),
        featureSettings.listPersonTargetFeatures({
          clientID: client.ID,
          featureID: NAME_THAT_CAMPER_STAFF_FEATURE_ID,
        }),
      ])

    const load = async (): Promise<void> => {
      let [staffResponse, accessResponse] = await fetchAll()
      if (cancelled) return

      // The roster is read through the persons service, whose JWT lives in a
      // cookie that is only refreshed on a coarse timer. When that token
      // briefly lapses the request fails authorization even for a fully
      // privileged user (AB#19213 — surfaces as a "not authorized to access
      // records for personID" error that a manual page refresh clears).
      // Re-exchange the session and retry once so we recover silently instead
      // of leaking a raw backend authorization string to staff.
      if (staffResponse.result !== 'success') {
        let refreshed: 'success' | 'login_required' | 'error' = 'error'
        try {
          refreshed = await handleRefreshSessionRef.current()
        } catch (e) {
          log.error(e instanceof Error ? e : new Error(JSON.stringify(e)))
        }
        if (cancelled) return
        if (refreshed === 'success') {
          ;[staffResponse, accessResponse] = await fetchAll()
          if (cancelled) return
        }
      }

      if (staffResponse.result !== 'success') {
        log.error(
          new Error(
            `CampanionStaffAccessView: staff load failed — ${staffResponse.error.message}`,
          ),
        )
        toastRef.current.error(copy.errors.loadFailed)
        setIsLoading(false)
        return
      }

      // Fail-open on the access list: if it errors, show all toggles as off
      // (cold-start default) rather than blocking the page. Surface a friendly
      // error via toast so the admin knows something went wrong; the raw
      // backend message is logged, not shown.
      // Keyed on string PersonID so HiredStaffMember.ID (string) and
      // PersonTargetFeatureRow.targetPersonID (number) meet at the same key
      // without ad-hoc casting at every lookup.
      const accessByTarget = new Map<
        string,
        {rowID: number; granterPersonID: number}
      >()
      if (accessResponse.result === 'success') {
        accessResponse.data.forEach((row) => {
          accessByTarget.set(row.targetPersonID.toString(), {
            rowID: row.id,
            granterPersonID: row.granterPersonID,
          })
        })
      } else {
        log.error(
          new Error(
            `CampanionStaffAccessView: access list load failed — ${accessResponse.error.message}`,
          ),
        )
        toastRef.current.error(copy.errors.accessLoadFailed)
      }

      setStaff(
        staffResponse.data.map((m) => {
          const access = accessByTarget.get(m.ID)
          return {
            ID: m.ID,
            name: m.fullName,
            years: m.years,
            nameThatCamper: access !== undefined,
            nameThatCamperRowID: access ? access.rowID : null,
            nameThatCamperGranterID: access ? access.granterPersonID : null,
          }
        }),
      )
      setIsLoading(false)
    }
    load()
    return (): void => {
      cancelled = true
    }
    // `toast` and `handleRefreshSession` are intentionally NOT dependencies —
    // they are read through refs (see above) so the effect cannot re-fire as a
    // side effect of the re-exchange it triggers. Re-running is keyed only on
    // the logical inputs.
  }, [client.ID, client.currentSeasonID, flagLoading, panelEnabled])

  const handleToggle = React.useCallback(
    async (row: StaffRow, nextChecked: boolean): Promise<void> => {
      markPending(row.ID)
      try {
        if (nextChecked) {
          const result = await featureSettings.createPersonTargetFeature({
            clientID: client.ID,
            // PersonID = staffer themselves (same as TargetPersonID) so the
            // row encodes "this staffer has permission to use this feature
            // on themselves" rather than pinning the row to the admin who
            // toggled it on.
            granterPersonID: row.ID,
            targetPersonID: row.ID,
            featureID: NAME_THAT_CAMPER_STAFF_FEATURE_ID,
          })
          if (result.result === 'success') {
            patchRow(row.ID, {
              nameThatCamper: true,
              nameThatCamperRowID: result.data.id,
              nameThatCamperGranterID: result.data.granterPersonID,
            })
          } else {
            log.error(
              new Error(
                `CampanionStaffAccessView: grant failed — ${result.error.message}`,
              ),
            )
            toastRef.current.error(copy.errors.toggleFailed)
          }
          return
        }

        if (
          row.nameThatCamperRowID === null ||
          row.nameThatCamperGranterID === null
        ) {
          // Defensive canary: only reachable if row state is inconsistent
          // (toggle says on but the IDs needed to revoke were never captured).
          // Without the toast the failure is invisible — Switch reverts and
          // nothing happens.
          toastRef.current.error(
            'Unable to revoke access — missing row data. Please refresh.',
          )
          return
        }
        const result = await featureSettings.deletePersonTargetFeature({
          id: row.nameThatCamperRowID,
          clientID: client.ID,
          granterPersonID: row.nameThatCamperGranterID,
        })
        if (result.result === 'success') {
          patchRow(row.ID, {
            nameThatCamper: false,
            nameThatCamperRowID: null,
            nameThatCamperGranterID: null,
          })
        } else {
          log.error(
            new Error(
              `CampanionStaffAccessView: revoke failed — ${result.error.message}`,
            ),
          )
          toastRef.current.error(copy.errors.toggleFailed)
        }
      } finally {
        clearPending(row.ID)
      }
    },
    // toast is read via toastRef (its identity is unstable under Chakra v1), so
    // it stays out of the deps — keeps handleToggle's identity stable, which in
    // turn stops the ToggleCell/columns memos from churning every render.
    [client.ID, markPending, clearPending, patchRow],
  )

  // Bulk revoke: turn Name That Camper off for every staffer who currently has
  // it on, across the whole roster (not just the filtered view). Each revoke is
  // an independent DELETE; we fire them together and patch rows as they succeed
  // so a partial failure still turns off everyone it could. The type predicate
  // narrows away the null row/granter IDs so the DELETE args stay number-typed.
  const handleTurnAllOff = React.useCallback(async (): Promise<void> => {
    const targets = staff.filter(
      (
        r,
      ): r is StaffRow & {
        nameThatCamperRowID: number
        nameThatCamperGranterID: number
      } =>
        r.nameThatCamper &&
        r.nameThatCamperRowID !== null &&
        r.nameThatCamperGranterID !== null,
    )
    if (targets.length === 0) return
    targets.forEach((r) => markPending(r.ID))
    try {
      const outcomes = await Promise.all(
        targets.map(async (r) => ({
          row: r,
          result: await featureSettings.deletePersonTargetFeature({
            id: r.nameThatCamperRowID,
            clientID: client.ID,
            granterPersonID: r.nameThatCamperGranterID,
          }),
        })),
      )
      let failures = 0
      outcomes.forEach(({row, result}) => {
        if (result.result === 'success') {
          patchRow(row.ID, {
            nameThatCamper: false,
            nameThatCamperRowID: null,
            nameThatCamperGranterID: null,
          })
        } else {
          failures += 1
        }
      })
      if (failures === 0) {
        toast.success(copy.bulkTurnOff.successToast)
      } else {
        toast.error(
          copy.bulkTurnOff.errorToast.replace('{count}', failures.toString()),
        )
      }
    } finally {
      targets.forEach((r) => clearPending(r.ID))
    }
  }, [staff, client.ID, markPending, clearPending, patchRow, toast])

  const handleTurnAllOffClick = React.useCallback((): void => {
    showConfirmation({
      variant: 'danger',
      title: copy.bulkTurnOff.confirmTitle,
      description: copy.bulkTurnOff.confirmDescription.replace(
        '{camp}',
        client.name,
      ),
      confirmButtonText: copy.bulkTurnOff.confirmButtonText,
      testID: 'staff-access-turn-all-off-confirm',
      onConfirm: handleTurnAllOff,
    })
  }, [showConfirmation, client.name, handleTurnAllOff])

  const ToggleCell = React.useCallback(
    (props: {row: {original: StaffRow}}): JSX.Element => (
      <Switch
        isChecked={props.row.original.nameThatCamper}
        isDisabled={pending.has(props.row.original.ID)}
        onChange={(e): void => {
          handleToggle(props.row.original, e.target.checked)
        }}
        data-testid={`staff-name-that-camper-toggle-${props.row.original.ID}`}
      />
    ),
    [handleToggle, pending],
  )

  const columns = React.useMemo<Column<StaffRow>[]>(
    () => [
      {
        id: 'name',
        Header: copy.columns.name,
        accessor: 'name',
        width: '80%',
        minWidth: 160,
        style: {textAlign: 'left', flexGrow: 1},
        cellStyle: {flexGrow: 1},
      },
      // Email column intentionally omitted this revision: the roster is resolved
      // via persons GetNames, which returns no contact details. Re-add here when
      // a staff-scoped email read is available.
      {
        id: 'nameThatCamper',
        Header: copy.columns.nameThatCamper,
        accessor: 'nameThatCamper',
        width: '20%',
        minWidth: 140,
        // Sort by toggle state (off < on). 'basic' compares the raw boolean
        // accessor value rather than alphanumerically stringifying it.
        sortType: 'basic',
        style: {textAlign: 'center'},
        cellStyle: {textAlign: 'center', justifyContent: 'center'},
        Cell: ToggleCell,
      },
    ],
    [ToggleCell],
  )

  const pageHeader = (
    <PageHeader
      title={copy.title}
      logContext='CampanionStaffAccessView'
      description={copy.description}
      callToAction={
        <Button
          variant='outline'
          onClick={(): void => setVideoOpen(true)}
          data-testid='staff-access-how-it-works-button'>
          {copy.video.buttonText}
        </Button>
      }
    />
  )

  if (flagLoading) {
    return <LoadingState>Loading...</LoadingState>
  }

  if (!panelEnabled) {
    return <PageNotFoundView />
  }

  const visibleStaff = firstYearOnly
    ? staff.filter((r) => r.years === 1)
    : staff
  const isEmpty = staff.length === 0
  const isFilteredEmpty = !isEmpty && visibleStaff.length === 0
  // Bulk turn-off operates on the whole roster, so gate on `staff` (not the
  // filtered view) and disable while any per-row request is in flight.
  const anyEnabled = staff.some((r) => r.nameThatCamper)
  const anyPending = pending.size > 0

  return (
    <PageTemplate
      header={pageHeader}
      width={['100%']}
      maxWidth={['1030px']}
      borderX={['0px']}>
      <Flex direction='column' px='.75em' py={1}>
        <Alert
          status='warning'
          variant='left-accent'
          flexDirection='column'
          alignItems='flex-start'
          mx='0.2em'
          mt={2}
          borderRadius={3}
          data-testid='staff-access-experimental-notice'>
          <Flex alignItems='center'>
            <AlertIcon />
            <AlertTitle>{copy.experimentalNotice.title}</AlertTitle>
          </Flex>
          <AlertDescription mt={1}>
            {copy.experimentalNotice.body}
          </AlertDescription>
          <AlertDescription mt={1}>
            {copy.experimentalNotice.contactNote}
          </AlertDescription>
        </Alert>
        <Divider mb='4' opacity={1} mx='0.2em' mt={4} />
        <Visible when={!isLoading && !isEmpty}>
          <Flex
            alignItems='center'
            justifyContent='space-between'
            mb={3}
            mx='0.2em'>
            <FormControl display='flex' alignItems='center' width='auto'>
              <Switch
                id='campanion-staff-first-year-only'
                isChecked={firstYearOnly}
                onChange={(e): void => setFirstYearOnly(e.target.checked)}
                data-testid='staff-first-year-only-filter'
                mr={2}
              />
              <FormLabel
                htmlFor='campanion-staff-first-year-only'
                mb={0}
                fontWeight='normal'>
                {copy.filters.firstYearOnly}
              </FormLabel>
            </FormControl>
            <Button
              variant='outline'
              colorScheme='red'
              size='sm'
              isDisabled={!anyEnabled || anyPending}
              onClick={handleTurnAllOffClick}
              data-testid='staff-access-turn-all-off-button'>
              {copy.bulkTurnOff.buttonText.replace('{camp}', client.name)}
            </Button>
          </Flex>
        </Visible>
        <Visible when={isLoading}>
          <LoadingState>Loading staff...</LoadingState>
        </Visible>
        <Visible when={!isLoading && isEmpty}>
          <BlankSlate iconName='people'>
            No hired staff were found for the current season.
          </BlankSlate>
        </Visible>
        <Visible when={!isLoading && isFilteredEmpty}>
          <BlankSlate iconName='people'>
            No first-year staff were found for the current season.
          </BlankSlate>
        </Visible>
        <Visible when={!isLoading && !isEmpty && !isFilteredEmpty}>
          <Table<StaffRow>
            name='CampanionStaffAccess'
            sortBy={sort}
            setSort={setSort}
            columns={columns}
            data={visibleStaff}
            pageSize={25}
          />
        </Visible>
      </Flex>
      <HowItWorksVideoModal
        isOpen={videoOpen}
        onClose={(): void => setVideoOpen(false)}
      />
    </PageTemplate>
  )
}
